/**
 * "Confirm your business details" — the first thing we need, and the one that
 * blocks the most downstream work (phone routing, service-area pages, hours in
 * schema markup).
 */

import React, { useState } from 'react';
import { Field, Panel, PanelBody, PanelHeader } from '../../ui/kit';
import { CopyRow, TaskDone, TaskShell, TextInput } from '../TaskShell';
import type { SetupStatus } from '../../../lib/setupFlow';
import type { TaskRecord } from '../../../lib/setupTasks';

const SUPPORT_EMAIL = 'hello@foundlio.com';

export default function ConfirmDetailsTask({
  status,
  record,
  onBack,
  onComplete,
}: {
  status: SetupStatus;
  record?: TaskRecord;
  onBack: () => void;
  onComplete: (data: Record<string, string>) => void;
}) {
  const [businessName, setBusinessName] = useState(record?.data?.businessName ?? status.businessName);
  const [phone, setPhone] = useState(record?.data?.phone ?? '');
  const [serviceArea, setServiceArea] = useState(record?.data?.serviceArea ?? '');
  const [hours, setHours] = useState(record?.data?.hours ?? 'Mon–Fri 8am–5pm');
  const [emergency, setEmergency] = useState(record?.data?.emergency === 'yes');
  const [error, setError] = useState('');

  const done = Boolean(record?.completedAt);

  const summary = [
    `Business: ${businessName}`,
    `Phone: ${phone}`,
    `Service area: ${serviceArea}`,
    `Hours: ${hours}${emergency ? ' · 24/7 emergency calls' : ''}`,
  ].join('\n');

  const mailto = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    `Business details ${status.orderRef}`
  )}&body=${encodeURIComponent(summary)}`;

  if (done) {
    return (
      <TaskShell
        eyebrow="Step · your details"
        title="Details confirmed."
        onBack={onBack}
      >
        <TaskDone
          title="We've got what we need"
          detail="Your pages, call routing and hours are built from this. Change it any time from your account."
          onBack={onBack}
          extra={
            <Panel className="text-left">
              <PanelHeader title="What you told us" />
              <PanelBody className="py-2">
                <CopyRow label="Business" value={businessName} />
                <CopyRow label="Phone" value={phone || '—'} />
                <CopyRow label="Service area" value={serviceArea || '—'} />
                <CopyRow label="Hours" value={`${hours}${emergency ? ' · 24/7 emergency' : ''}`} />
              </PanelBody>
            </Panel>
          }
        />
      </TaskShell>
    );
  }

  function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!businessName.trim()) {
      setError('We need the name as customers know it.');
      return;
    }
    if (!phone.trim()) {
      setError('A phone number is required — it is what the whole site points at.');
      return;
    }
    onComplete({
      businessName: businessName.trim(),
      phone: phone.trim(),
      serviceArea: serviceArea.trim(),
      hours: hours.trim(),
      emergency: emergency ? 'yes' : 'no',
      summary: `${businessName.trim()} · ${phone.trim()}`,
    });
  }

  return (
    <TaskShell
      eyebrow="Step · your details"
      title="Confirm your business details."
      description="Four things. Everything on the site — the header, the call button, the service-area pages — is built from these."
      onBack={onBack}
    >
      <form onSubmit={submit}>
        <Panel>
          <PanelBody className="space-y-5">
            <Field label="Business name" hint="Exactly as customers know you." htmlFor="business-name">
              <TextInput
                id="business-name"
                value={businessName}
                onChange={(event) => setBusinessName(event.target.value)}
                placeholder="Valley Plumbing & Drain"
              />
            </Field>

            <Field label="Phone for calls" hint="Every call button on the site rings this number." htmlFor="phone">
              <TextInput
                id="phone"
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                placeholder="(608) 555-0134"
                inputMode="tel"
              />
            </Field>

            <Field label="Service area" hint="Towns or counties you cover. We build a page per area." htmlFor="area">
              <TextInput
                id="area"
                value={serviceArea}
                onChange={(event) => setServiceArea(event.target.value)}
                placeholder="Madison, Middleton, Sun Prairie"
              />
            </Field>

            <Field label="Opening hours" htmlFor="hours">
              <TextInput
                id="hours"
                value={hours}
                onChange={(event) => setHours(event.target.value)}
                placeholder="Mon–Fri 8am–5pm"
              />
            </Field>

            <label className="flex items-center gap-3 text-sm text-primary">
              <input
                type="checkbox"
                checked={emergency}
                onChange={(event) => setEmergency(event.target.checked)}
                className="h-4 w-4 rounded border-zinc-300 text-primary focus:ring-0"
              />
              We take emergency calls 24/7
            </label>

            {error && <p className="text-[13px] text-red-700">{error}</p>}
          </PanelBody>
        </Panel>

        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button
            type="submit"
            className="rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            Save details
          </button>
          <a
            href={mailto}
            className="font-mono text-[11px] uppercase tracking-eyebrow text-zinc-500 transition-colors hover:text-primary"
          >
            Or email them to us →
          </a>
        </div>
      </form>
    </TaskShell>
  );
}
